import { Box, Chip, Typography } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import React, { useCallback, useMemo, useState } from "react"

import EditPaymentType from "../../../dialogs/EditPaymentTypeDialog"
import useCompanyPaymentTypes from "../../../hooks/useCompanyPaymentTypes"
import { getPaymentMethodLabel } from "../../../utils/paymentMethodLabels"

import PaymentActions from "./actions/PaymentActions"
import { ApprovePaymentType } from "./ApprovePaymentType"
import BankPaymentFields from "./BankPaymentFields"
import NonBankingPaymentMethod from "./methods/NonBankingPaymentMethod"
import PaymentTypeCardDetails from "./PaymentTypeCardDetails"

import { Button } from "@/components/common/Button"
import { CancelDialog, ConfirmationDialog } from "@/components/common/Modals"
import { PermissionActionType } from "@/constants/permissions"
import {
  type CompanyPaymentType,
  type CompanyPaymentTypeMethod,
  type CompanyPaymentTypeUpdateInput,
  CompanyStatus,
  PaymentMethod,
} from "@/graphql/codegen/graphql"
import { useDialogState } from "@/hooks/useDialogState"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { NotificationType } from "@/schemas/notification"
import { getCompanyStatusColor } from "@/screens/Companies/utils/company"
import { useCurrentUserStore } from "@/stores/currentUserStore"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { AddressFormat } from "@/types/common"
import { formatLocationAddress } from "@/utils/utils"

interface PaymentTypeCardProps {
  paymentType: CompanyPaymentType
  sameTypeIndex?: number
  onDefaultChange?: (paymentMethod: CompanyPaymentTypeMethod) => void
  onRefetch?: () => void
  onDeleteType: (companyPaymentTypeId: string) => void
  onDeleteMethod: (companyPaymentMethodId: string) => void
  onToggleActivation: (methodId: string, currentStatus: CompanyStatus) => void
}

const PaymentTypeCard: React.FC<PaymentTypeCardProps> = ({
  paymentType,
  sameTypeIndex = 1,
  onDefaultChange,
  onRefetch,
  onDeleteType,
  onDeleteMethod,
  onToggleActivation,
}) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { updatePaymentType, updatePaymentTypeMethod } = useCompanyPaymentTypes({
    companyId: paymentType.companyId,
    pause: true,
  })
  const { hasPermissions } = usePermissionManager()

  const userId = useCurrentUserStore((state) => state.currentUser?.userId)

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)
  const [typeIdToDelete, setTypeIdToDelete] = useState<string | null>(null)
  const [methodIdToDelete, setMethodIdToDelete] = useState<string | null>(null)
  const [isDeactivatePaymentTypeWarningOpen, setIsDeactivatePaymentTypeWarningOpen] = useState(false)
  const {
    isDialogOpen: isEditPaymentTypeOpen,
    openDialog: handleEditPaymentType,
    closeDialog: handleClosePaymentTypeDialog,
  } = useDialogState({
    onSuccess: () => {
      onRefetch?.()
    },
  })
  const navigate = useNavigate({ from: "/app/companies/$companyId/edit/accounting" })

  const isBankingPaymentType =
    paymentType.paymentMethods?.[0]?.method === PaymentMethod.Wire ||
    paymentType.paymentMethods?.[0]?.method === PaymentMethod.Ach

  const handlePaymentTypeDelete = (typeIdToDelete?: string) => {
    if (typeIdToDelete) {
      setTypeIdToDelete(typeIdToDelete)
    }

    setIsDeleteConfirmationOpen(true)
  }

  const handlePaymentTypeMethodDelete = (methodIdToDelete?: string) => {
    if (methodIdToDelete) {
      setMethodIdToDelete(methodIdToDelete)
    }

    setIsDeleteConfirmationOpen(true)
  }

  const confirmDelete = () => {
    setIsDeleteConfirmationOpen(false)
    if (methodIdToDelete && paymentType?.paymentMethods?.length) {
      onDeleteMethod(methodIdToDelete)
    } else {
      onDeleteType(typeIdToDelete || paymentType.companyPaymentTypeId)
    }
    setTypeIdToDelete(null)
    setMethodIdToDelete(null)
  }

  const handlePaymentTypeSave = async ({
    locationIds,
    bankLocationId,
    bankId,
  }: {
    locationIds: string[]
    bankLocationId?: string
    bankId?: string
  }) => {
    const input: CompanyPaymentTypeUpdateInput = {
      companyPaymentTypeId: paymentType.companyPaymentTypeId,
      locationIds,
    }

    if (bankId) {
      input.bankId = bankId
    }

    if (bankLocationId) {
      input.bankLocationId = bankLocationId
    }

    await updatePaymentType({
      input,
    })
    onRefetch?.()
    handleClosePaymentTypeDialog()
  }

  const handleAddPaymentType = () => {
    navigate({ search: { companyPaymentTypeId: paymentType.companyPaymentTypeId } })
  }

  const getTitle = () => {
    if (isBankingPaymentType) {
      return `Bank [${sameTypeIndex}] Information`
    }
    const method = paymentType.paymentMethods?.[0]?.method ?? "Unknown Method"
    return `${getPaymentMethodLabel(method)} [${sameTypeIndex}] Information`
  }

  const getCardDetailsTitle = () => {
    if (isBankingPaymentType) {
      return paymentType.bank?.name || `Bank [${sameTypeIndex}]`
    }
    return "Payment Type"
  }

  const getCardDetailsSubtitle = () => {
    if (isBankingPaymentType) {
      return formatLocationAddress(paymentType.bankLocation, AddressFormat.LocationAndAddress)
    }
    const methods = paymentType.paymentMethods!
    return methods.map((method) => getPaymentMethodLabel(method!.method)).join(", ")
  }

  const approvePaymentType = useCallback(
    async (paymentMethodId: string) => {
      await updatePaymentTypeMethod({
        input: {
          paymentMethodId,
          status: CompanyStatus.Active,
        },
      })
      onRefetch?.()
    },
    [updatePaymentTypeMethod, onRefetch]
  )

  const renderApproveBlocks = useMemo(() => {
    const isAccountingUser = hasPermissions([PermissionActionType.ACCOUNTING_MAINTENANCE])

    if (!paymentType.paymentMethods || !isAccountingUser) {
      return null
    }

    return paymentType.paymentMethods
      .filter((method) => method?.status === CompanyStatus.Pending)
      .map((method) => {
        if (!method) {
          return null
        }

        const canCurrentUserApprovePaymentType = method.createdBy !== userId && method.updatedBy !== userId

        if (!canCurrentUserApprovePaymentType) {
          return null
        }

        return (
          <ApprovePaymentType
            key={`${method.companyPaymentMethodId}-approve-block`}
            approvePaymentType={approvePaymentType}
            paymentTypeMethod={method}
          />
        )
      })
  }, [paymentType.paymentMethods, userId, approvePaymentType, hasPermissions])

  const handleToggleMethodActivation = () => {
    if (paymentType.paymentMethods?.[0]) {
      onToggleActivation(paymentType.paymentMethods[0].companyPaymentMethodId, paymentType.paymentMethods[0].status)
    }
  }

  const handlePaymentTypeDeactivation = () => {
    setIsDeactivatePaymentTypeWarningOpen(true)
  }

  const updatePaymentTypeActivationStatus = useCallback(
    async (
      statusMapper: (method: CompanyPaymentTypeMethod) => CompanyStatus,
      successMessage: string,
      errorMessage: string
    ) => {
      setIsDeactivatePaymentTypeWarningOpen(false)

      const results = await Promise.all(
        (paymentType.paymentMethods || []).map((method) =>
          updatePaymentTypeMethod({
            input: {
              paymentMethodId: method!.companyPaymentMethodId,
              status: statusMapper(method!),
            },
          })
        )
      )

      const hasError = results.some((result) => result.error)
      if (hasError) {
        notify({
          message: errorMessage,
          type: NotificationType.Error,
        })
        results.forEach((result) => {
          if (result.error) {
            console.error(result.error)
          }
        })
        return
      }

      notify({
        message: successMessage,
        type: NotificationType.Success,
      })
      onRefetch?.()
    },
    [paymentType.paymentMethods, updatePaymentTypeMethod, notify, onRefetch]
  )

  const deactivatePaymentType = useCallback(async () => {
    await updatePaymentTypeActivationStatus(
      () => CompanyStatus.Inactive,
      "Payment type have been successfully deactivated",
      "Failed to deactivate payment type"
    )
  }, [updatePaymentTypeActivationStatus])

  const handlePaymentTypeActivation = useCallback(async () => {
    await updatePaymentTypeActivationStatus(
      (method) =>
        method.method === PaymentMethod.Ach || method.method === PaymentMethod.Wire
          ? CompanyStatus.Pending
          : CompanyStatus.Active,
      "Payment type have been successfully activated",
      "Failed to activate payment type"
    )
  }, [updatePaymentTypeActivationStatus])

  const getStatus = () => {
    const isInactive = paymentType.paymentMethods?.every((method) => method?.status === CompanyStatus.Inactive)
    return isInactive ? CompanyStatus.Inactive : paymentType.status
  }

  return (
    <>
      <Box className='w-full space-y-6 rounded-md bg-gray-100 p-6'>
        <Box className='flex justify-between'>
          <Box className='mb-2 flex items-center gap-3'>
            <Typography className='text-ml text-gray-900'>{getTitle()}</Typography>
            <Chip label={getStatus()} variant='outlined' color={getCompanyStatusColor(getStatus())} />
          </Box>

          <PaymentActions
            paymentTypeStatus={getStatus()}
            onDelete={handlePaymentTypeDelete}
            onEditPaymentType={handleEditPaymentType}
            onDeactivate={handlePaymentTypeDeactivation}
            onActivate={handlePaymentTypeActivation}
          />
        </Box>

        <PaymentTypeCardDetails
          title={getCardDetailsTitle()}
          subtitle={getCardDetailsSubtitle()}
          billingLocations={paymentType.billingLocations}
        />

        {renderApproveBlocks}

        {isBankingPaymentType ? (
          <BankPaymentFields
            paymentType={paymentType}
            onDelete={handlePaymentTypeMethodDelete}
            onDefaultChange={onDefaultChange}
            onRefetch={onRefetch}
            onToggleActivation={onToggleActivation}
          />
        ) : (
          <NonBankingPaymentMethod
            method={paymentType.paymentMethods![0]}
            onDefaultChange={onDefaultChange}
            toggleMethodActivation={handleToggleMethodActivation}
          />
        )}

        {isBankingPaymentType ? (
          <Box className='flex justify-end'>
            <Button appearance='outlined' startIcon={<AddOutlined />} onClick={handleAddPaymentType}>
              Additional Payment Type For This Bank
            </Button>
          </Box>
        ) : null}
      </Box>

      {isEditPaymentTypeOpen ? (
        <EditPaymentType
          companyId={paymentType.companyId}
          paymentType={paymentType}
          onClose={handleClosePaymentTypeDialog}
          onSave={handlePaymentTypeSave}
        />
      ) : null}

      {isDeactivatePaymentTypeWarningOpen ? (
        <ConfirmationDialog
          title='This will make this payment type and each its payment methods unavailable for transactions.'
          content='Do you wish to proceed?'
          closeLabel='Cancel'
          confirmLabel='Deactivate'
          onConfirm={deactivatePaymentType}
          onClose={() => setIsDeactivatePaymentTypeWarningOpen(false)}
        />
      ) : null}

      {isDeleteConfirmationOpen ? (
        <CancelDialog
          title='Delete Payment Type'
          message={`Are you sure you want to delete this payment ${typeIdToDelete ? "type" : "method"}?`}
          confirmLabel='Yes, Delete'
          closeLabel='Cancel'
          onConfirm={confirmDelete}
          onClose={() => setIsDeleteConfirmationOpen(false)}
        />
      ) : null}
    </>
  )
}

export default PaymentTypeCard
